/**
 * Lembar log taksiran LRLC: taksiran yang berlaku di satu sumur, berdampingan
 * dengan taksiran-taksiran sebelumnya, penggolongnya, dan kurva masukannya.
 *
 * Tiap taksiran ulang melahirkan versi baru set ESTIMATED; versi lama tidak
 * dihapus, dan di sanalah taksiran sebelumnya tinggal. Yang ditanyakan orang
 * sesudah menaksir ulang dengan model lapangan atau metode lain adalah **apa
 * yang berubah, dan di kedalaman mana** — jadi tiap taksiran lama membawa
 * strip tipis yang menandai selang tempat ia berbeda dari yang terbaru, dan
 * tabel di bawahnya menyebut porsinya.
 */

import { useMemo, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { count } from "../lib/plural";
import type { LrlcClass } from "../types";
import { Checkbox, Dropdown, Empty, Loading, Notice, TextField } from "./ui";
import {
  CurvePath, LRLC_POTENTIAL, Swatch, Track, classF1, niceTicks, pct,
  type Band, type ClassReport, type Trace,
} from "./LrlcEstimateViews";

export interface EstimatedWell {
  well_id: number;
  well: string;
  field: string;
  /** Berapa versi set ESTIMATED yang memuat taksiran ini. */
  sets: number;
  models: string[];
  latest: string;
}

export interface Estimate {
  version: number;
  /** Versi-versi sesudahnya yang membawa serta taksiran ini tanpa mengubahnya. */
  carried_to: number[];
  active: boolean;
  created_at: string;
  created_by: string;
  model: string;
  method: string;
  samples: number;
  thickness: Record<string, number>;
  bands: Band[];
  versus_truth?: { n: number; accuracy: number | null; f1_macro: number | null;
                   classes: ClassReport };
  versus_latest: { compared: number; changed: number; share: number | null;
                   spans: [number, number][] };
}

export interface HistoryPayload {
  well: string;
  well_id: number;
  field: string;
  depth_range: [number, number] | null;
  truth: Band[] | null;
  context: Record<"GR" | "RT" | "VSH" | "PHIE" | "RHOB" | "NPHI", Trace | null>;
  estimates: Estimate[];
}

/** Paling banyak sekian taksiran tergambar sekaligus; sisanya bisa dicentang. */
const SHOWN_AT_FIRST = 4;
const SHEET_H = 680;
/** Warna strip Δ: jauh dari warna kelas mana pun, termasuk merah tua Non-LC. */
const CHANGED = "#f28c28";

const label = (e: Estimate) => `v${e.version}`;
const when = (iso: string) => iso.slice(0, 16).replace("T", " ");

export default function EstimateLog({ projectId, classes }: {
  projectId: number;
  classes: LrlcClass[];
}) {
  const list = useAsync(
    (signal) => api.get<{ wells: EstimatedWell[] }>(
      "/api/log-models/estimated-wells/", { project: projectId }, signal),
    [projectId]);
  const [field, setField] = useState("");
  const [search, setSearch] = useState("");
  const [wellId, setWellId] = useState<number | null>(null);

  const wells = list.data?.wells ?? [];
  const fields = useMemo(
    () => [...new Set(wells.map((w) => w.field).filter(Boolean))].sort(), [wells]);
  const shown = wells.filter((w) =>
    (!field || w.field === field)
    && (!search || w.well.toUpperCase().includes(search.trim().toUpperCase())));
  // Bawaannya sumur yang paling sering ditaksir: di sanalah ada yang dibandingkan.
  const chosen = wellId ?? [...shown].sort((a, b) => b.sets - a.sets)[0]?.well_id ?? null;

  if (list.loading) return <Loading label="Finding the wells that were estimated…" />;
  if (list.error) return <Notice tone="error">{list.error}</Notice>;
  if (!wells.length) {
    return (
      <Empty>
        No well carries an LRLC estimate yet. Estimate some in the Estimate
        wells tab; each run is kept, so the next one can be compared with it.
      </Empty>
    );
  }

  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
      <div style={{ width: 230, flex: "none", display: "flex", flexDirection: "column", gap: 7 }}>
        <Dropdown label="Field" value={field}
                  options={[{ value: "", label: `Every field (${wells.length})` },
                            ...fields.map((name) => ({
                              value: name,
                              label: `${name} (${wells.filter((w) => w.field === name).length})`,
                            }))]}
                  onChange={(value) => { setField(value); setWellId(null); }} />
        <TextField label="Well name" value={search}
                   onChange={(value) => { setSearch(value); setWellId(null); }} />
        <div style={{ maxHeight: SHEET_H, overflowY: "auto", border: "1px solid var(--line)",
                      borderRadius: 7 }} role="listbox" aria-label="Estimated wells">
          {shown.map((w) => (
            <button
              key={w.well_id}
              type="button"
              role="option"
              aria-selected={w.well_id === chosen}
              onClick={() => setWellId(w.well_id)}
              title={w.models.join("\n")}
              style={{
                display: "flex", width: "100%", justifyContent: "space-between",
                padding: "5px 8px", border: "none", cursor: "pointer", textAlign: "left",
                background: w.well_id === chosen ? "var(--hover-soft)" : "transparent",
                font: `${w.well_id === chosen ? 600 : 400} 11px var(--font-sans)`,
                color: "var(--ink-2)",
              }}
            >
              <span>{w.well}</span>
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {w.sets > 1 ? `${w.sets} runs` : "1 run"}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {chosen === null
          ? <Empty>No well matches these filters.</Empty>
          : <WellHistory key={chosen} projectId={projectId} wellId={chosen} classes={classes} />}
      </div>
    </div>
  );
}

function WellHistory({ projectId, wellId, classes }: {
  projectId: number; wellId: number; classes: LrlcClass[];
}) {
  const history = useAsync(
    (signal) => api.get<HistoryPayload>("/api/log-models/estimate-history/",
                                        { project: projectId, well: wellId }, signal),
    [projectId, wellId]);
  const [hidden, setHidden] = useState<Set<number> | null>(null);
  const [top, setTop] = useState<number | null>(null);
  const [base, setBase] = useState<number | null>(null);

  if (history.loading) return <Loading label="Reading the estimates of this well…" />;
  if (history.error) return <Notice tone="error">{history.error}</Notice>;
  const h = history.data!;
  if (!h.depth_range) return <Empty>{h.well} has nothing to draw.</Empty>;

  const off = hidden
    ?? new Set(h.estimates.slice(SHOWN_AT_FIRST).map((e) => e.version));
  const visible = h.estimates.filter((e) => !off.has(e.version));
  const t = top ?? h.depth_range[0];
  const b = base ?? h.depth_range[1];
  const y = (md: number) => ((md - t) / Math.max(1e-6, b - t)) * SHEET_H;
  const colorOf = (code: number) => classes.find((c) => c.code === code)?.color ?? "#999";
  const labelOf = (code: number) => classes.find((c) => c.code === code)?.label ?? String(code);
  const present = new Set<number>();
  for (const e of visible) for (const band of e.bands) present.add(band.code);
  for (const band of h.truth ?? []) present.add(band.code);
  const codes = [...new Set(h.estimates.flatMap((e) => Object.keys(e.thickness)))]
    .map(Number).sort((p, q) => p - q);
  const latest = h.estimates[0];

  const bands = (list: Band[], width: number) => list
    .filter((band) => band.base >= t && band.top <= b)
    .map((band, i) => {
      const y0 = Math.max(0, y(band.top));
      const y1 = Math.min(SHEET_H, y(band.base));
      return (
        <rect key={i} x={0} width={width} y={y0} height={Math.max(0.6, y1 - y0)}
              fill={band.color}>
          <title>{`${band.label} · ${band.top.toFixed(1)}–${band.base.toFixed(1)} m`}</title>
        </rect>
      );
    });

  const c = h.context;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
                    font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
        <span style={{ color: "var(--ink-2)", font: "600 12px var(--font-sans)" }}>{h.well}</span>
        <span>{h.field || "no field"}</span>
        <span>{count(h.estimates.length, "estimate")}</span>
        <label>Top <input type="number" value={Math.round(t)} style={{ width: 70 }}
                          onChange={(e) => setTop(Number(e.target.value))} /></label>
        <label>Base <input type="number" value={Math.round(b)} style={{ width: 70 }}
                           onChange={(e) => setBase(Number(e.target.value))} /></label>
        <button type="button" onClick={() => { setTop(null); setBase(null); }}
                style={{ font: "inherit", cursor: "pointer" }}>Whole well</button>
      </div>

      {h.estimates.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0 14px" }}
             aria-label="Estimates shown">
          {h.estimates.map((e) => (
            <div key={e.version} style={{ width: 250 }}>
              <Checkbox
                checked={!off.has(e.version)}
                label={`${label(e)} · ${when(e.created_at)}`}
                meta={e.active ? "current" : e.method}
                onChange={(on) => {
                  const next = new Set(off);
                  on ? next.delete(e.version) : next.add(e.version);
                  setHidden(next);
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
                    font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
        {[...present].sort((p, q) => p - q).map((code) => (
          <Swatch key={code} color={colorOf(code)} label={labelOf(code)} />
        ))}
        <Swatch color={CHANGED} label="Δ differs from the current estimate" />
      </div>

      <div style={{ display: "flex", gap: 4, alignItems: "flex-start", overflowX: "auto" }}
           aria-label="LRLC estimate log">
        <Track title="MD m" width={46} height={SHEET_H}>
          {niceTicks(t, b).map((tick) => (
            <text key={tick} x={42} y={y(tick) + 3} textAnchor="end"
                  style={{ font: "9px var(--font-mono)", fill: "var(--ink-5)" }}>{tick}</text>
          ))}
        </Track>
        {c.GR && (
          <Track title="GR" sub="0–150" width={84} height={SHEET_H}>
            <CurvePath trace={c.GR} y={y} top={t} base={b} min={0} max={150} log={false}
                       width={84} color="#3a8a3a" />
          </Track>
        )}
        {c.RT && (
          <Track title="RT" sub="0.2–200 log" width={84} height={SHEET_H}>
            <CurvePath trace={c.RT} y={y} top={t} base={b} min={0.2} max={200} log
                       width={84} color="#b33" />
          </Track>
        )}
        {(c.VSH || c.PHIE) && (
          <Track title="VSH · PHIE" sub="0–1 · 0.5–0" width={84} height={SHEET_H}>
            {c.VSH && <CurvePath trace={c.VSH} y={y} top={t} base={b} min={0} max={1}
                                 log={false} width={84} color="#8a6d3b" />}
            {c.PHIE && <CurvePath trace={{ ...c.PHIE,
                                           values: c.PHIE.values.map((v) => v === null ? null : 0.5 - v) }}
                                  y={y} top={t} base={b} min={0} max={0.5} log={false}
                                  width={84} color="#2a6fb0" />}
          </Track>
        )}
        {(c.RHOB || c.NPHI) && (
          <Track title="RHOB · NPHI" sub="1.95–2.95 · 0.45–−0.15" width={84} height={SHEET_H}>
            {c.RHOB && <CurvePath trace={c.RHOB} y={y} top={t} base={b} min={1.95} max={2.95}
                                  log={false} width={84} color="#c0392b" />}
            {c.NPHI && <CurvePath trace={{ ...c.NPHI,
                                           values: c.NPHI.values.map((v) => v === null ? null : 0.45 - v) }}
                                  y={y} top={t} base={b} min={0} max={0.6} log={false}
                                  width={84} color="#2d7dd2" />}
          </Track>
        )}
        {h.truth && (
          <Track title="Classifier" sub="LRLC_CLASS" width={70} height={SHEET_H}>
            {bands(h.truth, 70)}
          </Track>
        )}
        {visible.map((e) => (
          <div key={e.version} style={{ display: "flex", gap: 1 }}>
            <Track title={e === latest ? `${label(e)} current` : label(e)}
                   sub={e.method || "estimate"} width={70} height={SHEET_H}>
              {bands(e.bands, 70)}
            </Track>
            {e !== latest && (
              <Track title="Δ" width={12} height={SHEET_H}>
                {e.versus_latest.spans
                  .filter(([s0, s1]) => s1 >= t && s0 <= b)
                  .map(([s0, s1], i) => {
                    const y0 = Math.max(0, y(s0));
                    const y1 = Math.min(SHEET_H, y(s1));
                    return <rect key={i} x={0} width={12} y={y0}
                                 height={Math.max(0.8, y1 - y0)} fill={CHANGED} />;
                  })}
              </Track>
            )}
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", background: "var(--panel-inset)",
                        border: "1px solid var(--line)" }} aria-label="Estimates of this well">
          <thead>
            <tr>
              {["Estimate", "Made", "By", "Model", "Samples",
                ...(h.truth ? ["Match with classifier", "F1 LRLC potential"] : []),
                "Changed vs current",
                ...codes.map((code) => `${labelOf(code)} m`)].map((title, i) => (
                <th key={title} style={{ ...th, textAlign: i < 4 ? "left" : "right" }}>{title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {h.estimates.map((e) => (
              <tr key={e.version}>
                <td style={{ ...td, textAlign: "left", color: "var(--ink-2)" }}>
                  {label(e)}{e.active ? " (current)" : ""}
                  {e.carried_to.length > 0 && (
                    <span style={{ color: "var(--ink-6)" }}>
                      {" "}→ {e.carried_to.map((v) => `v${v}`).join(", ")}
                    </span>
                  )}
                </td>
                <td style={{ ...td, textAlign: "left" }}>{when(e.created_at)}</td>
                <td style={{ ...td, textAlign: "left" }}>{e.created_by || "—"}</td>
                <td style={{ ...td, textAlign: "left", maxWidth: 260, overflow: "hidden",
                             textOverflow: "ellipsis" }} title={e.model}>{e.model || "—"}</td>
                <td style={td}>{e.samples.toLocaleString("en-US")}</td>
                {h.truth && <td style={{ ...td, color: "var(--accent)" }}>
                  {pct(e.versus_truth?.accuracy)}</td>}
                {h.truth && <td style={td}>
                  {pct(classF1(e.versus_truth?.classes, LRLC_POTENTIAL))}</td>}
                <td style={td}>
                  {e === latest ? "—"
                    : `${pct(e.versus_latest.share)} (${e.versus_latest.changed.toLocaleString("en-US")})`}
                </td>
                {codes.map((code) => (
                  <td key={code} style={td}>{e.thickness[String(code)]?.toFixed(1) ?? "0.0"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)", maxWidth: 820 }}>
        Every estimate is kept: estimating a well again writes a new version of
        its ESTIMATED set and leaves the earlier one behind. A version that only
        carried an estimate forward, unchanged, while another curve was
        estimated is folded into the estimate it carried ("→ v3"). Δ marks the
        depths where an earlier estimate gives a different class from the
        current one.{h.truth ? " Match with classifier compares each estimate with LRLC_CLASS on the same samples." : ""}
      </span>
    </div>
  );
}

const td: React.CSSProperties = {
  padding: "4px 8px", textAlign: "right", font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
};
const th: React.CSSProperties = {
  ...td, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)", background: "var(--panel)",
};
